import React, { useEffect, useState } from 'react';
import { useBilling } from '../../../contexts/BillingContext';
import { useSettingsDraft } from '../../../hooks/useSettingsDraft';
import { NumberSeries } from '../../../types/billing';
import { seriesLabels } from '../../../data/billingDefaults';
import { formatNumber } from '../../../utils/billingFirestore';
import { Button } from '../../../components/ui/Button';
import { SettingsPanel } from './SettingsPanel';
const series = Object.keys(seriesLabels) as NumberSeries[];

/** Prefixes, padding and yearly sequences per document type, plus a controlled "next number". */
export function NumberingSettings() {
  const {
    readNextNumber,
    setNextNumber,
    config
  } = useBilling();
  const s = useSettingsDraft(['numbering'], 'Document numbering', d => {
    const prefixes = series.map(k => d.numbering[k].prefix.trim().toUpperCase());
    if (prefixes.some(p => !p)) return 'Every document type needs a prefix.';
    if (prefixes.some(p => !/^[A-Z0-9/-]+$/.test(p))) return 'Prefixes may use letters, numbers, "-" and "/" only.';
    if (new Set(prefixes).size !== prefixes.length) return 'Each document type needs its own prefix so numbers can never clash.';
    if (series.some(k => d.numbering[k].padding < 3 || d.numbering[k].padding > 10)) return 'Digits must be between 3 and 10.';
    return null;
  });
  const [next, setNext] = useState<Partial<Record<NumberSeries, number>>>({});
  const [edit, setEdit] = useState<Partial<Record<NumberSeries, string>>>({});
  const [msg, setMsg] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const load = () => series.forEach(k => readNextNumber(k).then(n => setNext(p => ({
    ...p,
    [k]: n
  }))).catch(() => undefined));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [config.numbering]);
  const applyNext = async (k: NumberSeries) => {
    const value = Number(edit[k]);
    const result = await setNextNumber(k, value);
    setMsg({
      ok: result.ok,
      text: result.ok ? result.message ?? 'Saved.' : result.error ?? 'Not saved.'
    });
    if (result.ok) {
      setEdit(e => ({
        ...e,
        [k]: undefined
      }));
      load();
    }
  };
  return <SettingsPanel title="Document numbering" description="Separate sequences for each document type. Numbers are reserved in the database when a document is saved, so two users can never receive the same number, and numbers already issued (including imported history) are skipped." {...s} onSave={s.save} onReset={s.reset} data-id="element-5396" data-mp="3es9f54flxm0d">
      <div className="overflow-x-auto" data-id="element-5397" data-mp="30vdq42iynjyo">
        <table className="min-w-full text-sm" data-id="element-5398" data-mp="2vuq11meald73">
          <thead className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-5399" data-mp="2vw15lofkaoz9"><tr data-id="element-5400" data-mp="39vixwds2gkdd"><th className="py-2 pr-3" data-id="element-5401" data-mp="39v974nz7696m">Document</th><th className="py-2 pr-3" data-id="element-5402" data-mp="34uv0nad8v7ap">Prefix</th><th className="py-2 pr-3" data-id="element-5403" data-mp="34ttiyn1q3mk0">Digits</th><th className="py-2 pr-3" data-id="element-5404" data-mp="34wzasa1ql2jf">Yearly</th><th className="py-2 pr-3" data-id="element-5405" data-mp="p37e88vuwx65">Next number</th><th className="py-2" data-id="element-5406" data-mp="p25p50i0aa5f">Change next number</th></tr></thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5407" data-mp="k5fb0iprhomn">
            {series.map(k => {
            const rule = s.draft.numbering[k];
            const set = (patch: Partial<typeof rule>) => s.replace('numbering', {
              ...s.draft.numbering,
              [k]: {
                ...rule,
                ...patch
              }
            });
            return <tr key={k} data-id="element-5408" data-mp="io9ybadhc475">
                  <td className="py-2 pr-3 font-medium text-slate-800" data-id="element-5409" data-mp="ip1hflpw49gj">{seriesLabels[k]}</td>
                  <td className="py-2 pr-3" data-id="element-5410" data-mp="nncsu0vu2s0v"><label className="sr-only" htmlFor={`nb-p-${k}`} data-id="element-5411" data-mp="nonvfxdjltqe">{seriesLabels[k]} prefix</label><input id={`nb-p-${k}`} value={rule.prefix} onChange={e => set({
                  prefix: e.target.value.toUpperCase()
                })} className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-id="element-5412" data-mp="1bcnv5msu0uh4" /></td>
                  <td className="py-2 pr-3" data-id="element-5413" data-mp="11m2ord6x2lnn"><label className="sr-only" htmlFor={`nb-d-${k}`} data-id="element-5414" data-mp="11ki3yfaemh2z">{seriesLabels[k]} digits</label><input id={`nb-d-${k}`} type="number" min={3} max={10} value={rule.padding} onChange={e => set({
                  padding: Number(e.target.value)
                })} className="w-16 rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-id="element-5415" data-mp="16jl35j3pdt31" /></td>
                  <td className="py-2 pr-3" data-id="element-5416" data-mp="afnndgcsnby0"><label className="inline-flex items-center gap-1.5" data-id="element-5417" data-mp="ag5q8vtyooew"><input type="checkbox" checked={Boolean(rule.yearly)} onChange={e => set({
                    yearly: e.target.checked
                  })} className="rounded border-slate-300" data-id="element-5418" data-mp="adt406h67rw1" /><span className="sr-only" data-id="element-5419" data-mp="1efqn2pxej61">Restart {seriesLabels[k]} each year</span></label></td>
                  <td className="whitespace-nowrap py-2 pr-3 font-mono text-xs" data-id="element-5420" data-mp="3smbrp400rv2m">{next[k] ? formatNumber({
                  ...config,
                  numbering: s.draft.numbering
                }, k, next[k]!) : '…'}</td>
                  <td className="py-2" data-id="element-5421" data-mp="p4tias4lmg03">
                    <div className="flex gap-1.5" data-id="element-5422" data-mp="p5uzlec7jyq9">
                      <label className="sr-only" htmlFor={`nb-n-${k}`} data-id="element-5423" data-mp="ke3gfovdsva5">New next number for {seriesLabels[k]}</label>
                      <input id={`nb-n-${k}`} type="number" min={1} value={edit[k] ?? ''} onChange={e => setEdit(p => ({
                    ...p,
                    [k]: e.target.value
                  }))} placeholder={String(next[k] ?? '')} className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm" disabled={s.dirty} data-id="element-5424" data-mp="ffa3bxk0b6ai" />
                      <Button size="sm" variant="outline" disabled={!edit[k] || s.dirty} onClick={() => applyNext(k)} data-id="element-5425" data-mp="1feu11mx3e53j">Set</Button>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
      <ul className="list-disc space-y-1 pl-5 text-xs text-slate-500" data-id="element-5426" data-mp="1pboy01dpvxfp">
        <li data-id="element-5427" data-mp="1ud4fjjgorltc">Yearly numbering adds the Lusaka year (e.g. INV-2026-000001) and restarts at 1 every 1 January — the only automatic reset.</li>
        <li data-id="element-5428" data-mp="1yblqke389gg1">"Set" only moves the sequence. A number that has already been issued is rejected, and issued numbers are always skipped. Save prefix changes first.</li>
        <li data-id="element-5429" data-mp="284stzteeqmdh">Prefix changes apply to new documents only. Existing documents keep their numbers.</li>
      </ul>
      {msg && <p role={msg.ok ? 'status' : 'alert'} className={`text-sm ${msg.ok ? 'text-emerald-700' : 'text-red-700'}`} data-id="element-5430" data-mp="2d1szo37chn42">{msg.text}</p>}
    </SettingsPanel>;
}